/**
 * SEQ-93: Reconciliation Status Types & Helpers
 *
 * Manual variation reconciliation status, used in the Variations Tab
 * to tag legacy variations without affecting financial values.
 *
 * Color palette uses the app's semantic color system:
 * - Null   → neutral gray  (matches --text-muted / --bg-tertiary)
 * - Recon  → success green  (matches --color-success)
 * - Held   → warning amber  (matches --color-warning)
 * - Paid   → brand blue     (matches --seq-blue)
 */

export type ReconciliationStatus = 'reconciled' | 'held' | 'paid' | null;

export interface ReconciliationOption {
  value: ReconciliationStatus;
  label: string;
  /** CSS class suffix for the select/badge modifier: recon-select--{key} */
  key: string;
  /** Dot indicator color */
  dot: string;
  color: string;
  bg: string;
  border: string;
}

export const RECONCILIATION_OPTIONS: ReconciliationOption[] = [
  {
    value: null,
    label: 'Not Reconciled',
    key: 'null',
    dot: '#94a3b8',
    color: '#475569',         // --text-secondary
    bg: '#e8f1fc',            // --bg-tertiary
    border: '#e2e8f0',        // --border-color
  },
  {
    value: 'reconciled',
    label: 'Reconciled',
    key: 'reconciled',
    dot: '#10b981',           // --color-success
    color: '#059669',
    bg: 'rgba(16, 185, 129, 0.08)',
    border: 'rgba(16, 185, 129, 0.3)',
  },
  {
    value: 'held',
    label: 'Held',
    key: 'held',
    dot: '#f59e0b',           // --color-warning
    color: '#b45309',
    bg: 'rgba(245, 158, 11, 0.08)',
    border: 'rgba(245, 158, 11, 0.3)',
  },
  {
    value: 'paid',
    label: 'Paid',
    key: 'paid',
    dot: '#1e73be',           // --seq-blue
    color: '#1a5a9a',
    bg: 'rgba(30, 115, 190, 0.08)',
    border: 'rgba(30, 115, 190, 0.3)',
  },
];

/** Get the option config for a given status */
export function getReconciliationOption(
  status: ReconciliationStatus,
): ReconciliationOption {
  return (
    RECONCILIATION_OPTIONS.find((opt) => opt.value === status) ??
    RECONCILIATION_OPTIONS[0]
  );
}

/** Human-readable label for a reconciliation status */
export function getReconciliationLabel(status: ReconciliationStatus): string {
  return getReconciliationOption(status).label;
}
